import { useState } from 'react';
import { Box, Chip, Grid, IconButton, Menu, MenuItem, Paper, Stack, Typography, styled } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import KeyIcon from '@mui/icons-material/Key';
import LanguageIcon from '@mui/icons-material/Language';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import Tooltip from 'components/popups/Tooltip';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';
import { formatPrice, abbreviateNumber } from 'utility/numbers';
import { getSalesStatus, SALES_STATUS } from '../productFilters';

export default function ProductList({
    addon,
    event,
    products,
    compact = false,
    onProductSelect,
    onProductCopy,
}) {
    const emptyMessage = addon === true ? 'No add-ons found' : 'No tickets found';
    const [menu, setMenu] = useState({ anchorEl: null, productId: null });

    const handleMenuOpen = (event, productId) => {
        setMenu({ anchorEl: event.currentTarget, productId });
    };

    const handleMenuClose = () => {
        setMenu({ anchorEl: null, productId: null });
    };

    const handleCopy = () => {
        onProductCopy(menu.productId);
        handleMenuClose();
    };

    const handleEdit = () => {
        onProductSelect(menu.productId);
        handleMenuClose();
    };

    return (
        <Box>
            {products.length === 0 && (
                <EmptyState elevation={0}>
                    <Typography>{emptyMessage}</Typography>
                </EmptyState>
            )}

            {products.map((product) => (
                <ProductRow
                    key={product.product_id}
                    container
                    inactive={isProductInactive(product, event?.time_zone) ? 1 : 0}
                >

                    <Grid item xs={12} sm={12} lg={7} sx={{ minWidth: 0 }}>
                        <ProductsTableTitle
                            product={product}
                            event={event}
                            addon={addon}
                            compact={compact}
                            onEdit={() => onProductSelect(product.product_id)}
                        />
                    </Grid>

                    <Grid item xs={2} sm={4} lg={1}>
                        <MetricCell label='ONLINE' text={product.price === 0 ? 'FREE' : formatPrice(product.price, { minimumFractionDigits: 0 })} />
                    </Grid>

                    <Grid item xs={3} sm={2} lg={1}>
                        <MetricCell
                            label='POS'
                            text={product.pos_visible
                                ? product.pos_price === null
                                    ? product.price === 0 ? 'FREE' : formatPrice(product.price, { minimumFractionDigits: 0 })
                                    : product.pos_price === 0 ? 'FREE' : formatPrice(product.pos_price, { minimumFractionDigits: 0 })
                                : 'POS OFF'}
                        />
                    </Grid>

                    <Grid item xs={3} sm={2} lg={1}>
                        <MetricCell label='SOLD' text={`${abbreviateNumber(product.qty_sold, 0, 1, true)} / ${abbreviateNumber(product.total_available, 0, 1, true)}`} />
                    </Grid>

                    <Grid item xs={3} sm={2} lg={1}>
                        <MetricCell label='SCANNED' text={abbreviateNumber(product.scan_count)} />
                    </Grid>

                    <Grid item xs={1} sm={2} lg={1}>
                        <Box display={'flex'} justifyContent={'flex-end'}>
                            <IconButton
                                onClick={(event) => handleMenuOpen(event, product.product_id)}
                                size="small"
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                </ProductRow>
            ))}

            <Menu anchorEl={menu.anchorEl} open={Boolean(menu.anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleCopy}>Copy</MenuItem>
                <MenuItem onClick={handleEdit}>Edit</MenuItem>
            </Menu>
        </Box>
    );
}

export function isProductInactive(product, timezone) {
    if (product.visible === 0) {
        return true;
    }

    if (!product.sale_end_date) {
        return false;
    }

    return moment(product.sale_end_date).tz(timezone, true).isBefore(moment());
}

function MetricCell(props) {
    const { label, text } = props;

    return (
        <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
            <Typography fontSize={12} variant='caption'>{label}</Typography>
            <Typography fontSize={14} fontWeight={'bold'} textAlign={'end'}>{text}</Typography>
        </Box>
    )
}

function ProductsTableTitle({ addon, compact, event, product, onEdit }) {
    const typeLabel = addon ? 'ADD-ON' : 'ADMISSION';
    const updatedAt = product.updated_at ? `Edited ${getRelativeTime(product.updated_at, 'short')}` : null;

    let hoverMessage = null;

    if (product.updated_at && product.updated_by) {
        hoverMessage = `Last edited by ${product.updated_by} on ${moment(product.updated_at).format('dddd, MMMM D, h:mma')}`;
    }

    return (
        <Box mb={[2, 2, 2, 0]}>
            <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
                <TitleText onClick={onEdit ?? undefined}>
                    {product.prod_name}
                </TitleText>
                {!compact && (<>
                    <TypePill label={typeLabel} size="small" />
                    {updatedAt && (
                        <Tooltip message={hoverMessage}>
                            <Typography variant="caption" color="text.secondary">
                                {updatedAt}
                            </Typography>
                        </Tooltip>
                    )}
                </>)}
            </Stack>

            {!compact && (
                <>
                    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" mt={1.5}>
                        <AudiencePill product={product} />
                        <SaleStatusPill
                            startDate={product.sale_start_date}
                            endDate={product.sale_end_date}
                            timezone={event?.time_zone}
                            qtyLeft={product.qty_left}
                            forceGray={product.visible === 0}
                        />
                        <ScanLabelPill product={product} />
                    </Stack>

                    {Boolean(product.prod_group_name || product.label) && (
                        <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" mt={1}>
                            <GroupPill name={product.prod_group_name} />
                            <LabelPill name={product.label} colorHex={product.color_hex} />
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
}

function GroupPill({ name }) {
    if (name == null || name === '') {
        return null;
    }

    return (
        <Tooltip message={`Ticket Group: ${name}`}>
            <MetaPill icon={<FolderOutlinedIcon />} label={name} pillcolor="group" />
        </Tooltip>
    );
}

function LabelPill({ name, colorHex }) {
    if (!name || !colorHex) {
        return null;
    }

    const label = 'Check-in: ' + name;
    const dotColor = `#${String(colorHex).replace(/^#/, '')}`;

    return (
        <Tooltip message='Check-in direction shown to scanner volunteers after this ticket is scanned'>
            <MetaPill icon={<LabelDotIcon dotcolor={dotColor} />} label={label} pillcolor="label" />
        </Tooltip>
    );
}

function SaleStatusPill({ startDate, endDate, timezone, qtyLeft, forceGray = false }) {
    const now = moment();
    const saleStart = startDate ? moment(startDate).tz(timezone, true) : null;
    const saleEnd = endDate ? moment(endDate).tz(timezone, true) : null;
    const salesStatus = getSalesStatus(
        { sale_start_date: startDate, sale_end_date: endDate, qty_left: qtyLeft },
        timezone,
        now,
    );

    if (!salesStatus) {
        // Missing info
        return null;
    }

    // SOLD OUT
    if (salesStatus === SALES_STATUS.soldOut) {
        return (
            <Tooltip message={`Sold out: Sales were scheduled to end ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}>
                <MetaPill icon={<AccessTimeIcon />} label="Sold Out" pillcolor={forceGray ? 'gray' : 'red'} />
            </Tooltip>
        )
    }

    // SALES START IN FUTURE
    if (saleStart > now) {
        return (
            <Tooltip message={`Sales start ${saleStart.format('dddd, MMMM D')} at ${saleStart.format('hh:mm a')}`}>
                <MetaPill
                    icon={<AccessTimeIcon />}
                    label={`Starts ${saleStart.format('MMM D')} • ${saleStart.format('hh:mm a')}`}
                    pillcolor={forceGray ? 'gray' : 'blue'}
                />
            </Tooltip>
        );
    }

    // SALES CONCLUDED
    if (saleEnd < now) {
        return (
            <Tooltip message={`Sales ended ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}>
                <MetaPill
                    icon={<AccessTimeIcon />}
                    label={`Sale Ended`}
                    pillcolor="gray"
                />
            </Tooltip>
        );
    }

    // SALES ONGOING
    return (
        <Tooltip message={`Sales end ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}>
            <MetaPill
                icon={<AccessTimeIcon />}
                label={`On Sale • ${getRelativeTime(saleEnd, 'short')} left`}
                pillcolor={forceGray ? 'gray' : 'green'}
            />
        </Tooltip>
    );
}

function AudiencePill({ product }) {
    if (product.access_code) {
        return (
            <Tooltip message={`Buyers need to enter access code ${product.access_code} to purchase this ticket`}>
                <MetaPill icon={<KeyIcon />} label="Access Code" pillcolor="gold" />
            </Tooltip>
        )
    }

    if (product.visible === 1) {
        return (
            <Tooltip message={'This ticket is available to anyone with the buy link'}>
                <MetaPill icon={<LanguageIcon />} label="Everyone" pillcolor="green" />
            </Tooltip>
        )
    }

    return (
        <Tooltip message={'This ticket is not available for online purchase'}>
            <MetaPill icon={<VisibilityOffIcon />} label="Offline" pillcolor="gray" />
        </Tooltip>
    )
}

function ScanLabelPill({ product }) {
    const scanStart = product.scan_start_date ? moment(product.scan_start_date) : null;
    const scanEnd = product.scan_end_date ? moment(product.scan_end_date) : null;

    if (!scanStart || !scanEnd) {
        // Missing Info
        return null;
    }

    const singleDay = scanStart.isSame(scanEnd, 'day') || scanEnd.diff(scanStart, 'hour') < 18;

    if (product.unlimited_entry === 1) {
        // Unlimited Scan
        if (singleDay) {
            return (
                <Tooltip message={`Unlimited scanning ${scanStart.format('dddd, MMMM D')} ${scanStart.format('hh:mm a')} to ${scanEnd.format('hh:mm a')}`}>
                    <MetaPill icon={<QrCodeScannerIcon />} label={`Unlimited use ${scanStart.format('ddd h:mma')} - ${scanEnd.format('h:mma')}`} pillcolor="gold" />
                </Tooltip>
            )
        } else {
            return (
                <Tooltip message={`Unlimited scanning from ${scanStart.format('dddd, MMMM D')} at ${scanStart.format('hh:mm a')} until ${scanEnd.format('dddd, MMMM D')} at ${scanEnd.format('hh:mm a')}`}>
                    <MetaPill icon={<QrCodeScannerIcon />} label={`Unlimited use ${scanStart.format('MMM D')} - ${scanEnd.format('MMM D')}`} pillcolor="gold" />
                </Tooltip>
            )
        }

    }
    else if (product.multi_day_entry === 1) {
        // Multi Scan
        return (
            <Tooltip message={`Can be scanned daily from ${scanStart.format('dddd, MMMM D')} at ${scanStart.format('hh:mm a')} until ${scanEnd.format('dddd, MMMM D')} at ${scanEnd.format('hh:mm a')}`}>
                <MetaPill icon={<QrCodeScannerIcon />} label={`Scan Daily ${scanStart.format('MMM D')} - ${scanEnd.format('MMM D')}`} pillcolor="blue" />
            </Tooltip>
        )
    } else {
        // Single Scan
        if (singleDay) {
            // One Day
            return (
                <Tooltip message={`Can be scanned once ${scanStart.format('dddd, MMMM D')} ${scanStart.format('hh:mm a')} to ${scanEnd.format('hh:mm a')}`}>
                    <MetaPill icon={<QrCodeScannerIcon />} label={`Scan ${scanStart.format('ddd h:mma')} - ${scanEnd.format('h:mma')}`} pillcolor="blue" />
                </Tooltip>
            )
        } else {
            // Multi Day (U-Pick)
            return (
                <Tooltip message={`Can be scanned once between ${scanStart.format('dddd, MMMM D')} at ${scanStart.format('hh:mm a')} and ${scanEnd.format('dddd, MMMM D')} at ${scanEnd.format('hh:mm a')}`}>
                    <MetaPill icon={<QrCodeScannerIcon />} label={`Scan ${scanStart.format('MMM D')} - ${scanEnd.format('MMM D')}`} pillcolor="blue" />
                </Tooltip>
            )
        }
    }
}

const EmptyState = styled(Paper)(({ theme }) => ({
    minHeight: 128,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
}));

const ProductRow = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'inactive',
})(({ theme, inactive }) => ({
    padding: theme.spacing(2),
    backgroundColor: inactive ? '#fafafa' : '#fff',
    opacity: inactive ? 0.5 : 1,
    alignItems: 'flex-start',
    borderBottomWidth: 0.5,
    borderBottomColor: theme.palette.grey[300],
    borderBottomStyle: 'solid'
}));

const TitleText = styled(Typography)(({ theme }) => ({
    paddingRight: theme.spacing(0.5),
    display: 'inline',
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
        cursor: 'pointer',
    },
}));

const TypePill = styled(Chip)(({ theme }) => ({
    height: 20,
    borderRadius: 6,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`,
    '& .MuiChip-label': {
        fontSize: 12,
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: '0.08em',
        color: theme.palette.grey[700],
    },
}));

const LabelDotIcon = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'dotcolor',
})(({ dotcolor }) => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: dotcolor,
    flexShrink: 0,
}));

const MetaPill = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'pillcolor',
})(({ theme, pillcolor }) => {
    const styles = {
        green: {
            backgroundColor: '#DDF5E6',
            color: '#0F5132',
        },
        blue: {
            backgroundColor: '#DCEEFF',
            color: '#0B4F86',
        },
        gold: {
            backgroundColor: '#FDF1CC',
            color: '#8A5A00',
        },
        red: {
            backgroundColor: '#F9E8E4',
            color: '#B01212',
        },
        gray: {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[700],
        },
        group: {
            backgroundColor: '#DFE9FF',
            color: '#234A8F',
        },
        label: {
            backgroundColor: '#F2E4FF',
            color: '#6A2C91',
        },
    };

    return {
        height: 20,
        borderRadius: 12,
        backgroundColor: styles[pillcolor]?.backgroundColor,
        color: styles[pillcolor]?.color,
        '& .MuiChip-icon': {
            color: 'inherit',
            fontSize: 12,
        },
        '& .MuiChip-label': {
            fontSize: 12,
            fontWeight: theme.typography.fontWeightMedium,
        },
    };
});
